const readJunipProductReviewCount = (junipRoot) => {
  const countText = junipRoot
    ?.querySelector('.junip-product-review')
    ?.querySelector('.junip-product-review-count')
    ?.textContent || '';
  const countMatch = countText.match(/\d[\d,]*/);
  if (!countMatch) return null;
  const count = Number(countMatch[0].replaceAll(',', ''));
  return Number.isFinite(count) ? count : null;
};

(() => {
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const syncCarousel = (root) => {
    const track = root.querySelector('[data-fizo-carousel-track]');
    if (!track) return;
    const previous = root.querySelector('[data-fizo-carousel-prev]');
    const next = root.querySelector('[data-fizo-carousel-next]');
    const max = Math.max(track.scrollWidth - track.clientWidth, 0);
    const start = track.scrollLeft <= 2;
    const end = track.scrollLeft >= max - 2;
    previous?.toggleAttribute('disabled', start);
    next?.toggleAttribute('disabled', end);
    root.dataset.fizoCarouselState = start ? 'start' : end ? 'end' : 'middle';
  };

  const initCarousels = () => {
    document.querySelectorAll('[data-fizo-carousel]').forEach((root) => {
      if (root.dataset.fizoCarouselReady) return;
      root.dataset.fizoCarouselReady = 'true';
      const track = root.querySelector('[data-fizo-carousel-track]');
      if (!track) return;
      root.querySelector('[data-fizo-carousel-prev]')?.addEventListener('click', () => {
        track.scrollBy({ left: -track.clientWidth, behavior: reducedMotion ? 'auto' : 'smooth' });
      });
      root.querySelector('[data-fizo-carousel-next]')?.addEventListener('click', () => {
        track.scrollBy({ left: track.clientWidth, behavior: reducedMotion ? 'auto' : 'smooth' });
      });
      track.addEventListener('scroll', () => syncCarousel(root), { passive: true });
      new ResizeObserver(() => syncCarousel(root)).observe(track);
      syncCarousel(root);
    });
  };

  const initVideos = () => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting) video.play().catch(() => {});
        else video.pause();
      });
    }, { threshold: 0.35 });
    document.querySelectorAll('[data-fizo-autoplay-video], .fizo-pdp-video video').forEach((video) => observer.observe(video));
  };

  const dialog = () => document.querySelector('[data-fizo-lightbox]');
  const getPreviewItems = (scope) => [...scope.querySelectorAll('[data-fizo-preview-item]')];
  const renderPreview = (index) => {
    const node = dialog();
    if (!node) return;
    const items = node._items || [];
    if (!items.length) return;
    const item = items[clamp(index, 0, items.length - 1)];
    node._index = clamp(index, 0, items.length - 1);
    node.querySelector('[data-fizo-lightbox-image]').src = item.dataset.fizoPreviewSrc;
    node.querySelector('[data-fizo-lightbox-image]').alt = item.dataset.fizoPreviewAlt || '';
    node.querySelector('[data-fizo-lightbox-count]').textContent = `${node._index + 1} / ${items.length}`;
  };
  const openPreview = (trigger) => {
    const node = dialog();
    if (!node) return;
    const scope = trigger.closest('[data-fizo-preview-scope]') || document;
    node._items = getPreviewItems(scope);
    renderPreview(node._items.indexOf(trigger));
    node.showModal();
  };
  const initLightbox = () => {
    const node = dialog();
    if (!node || node.dataset.fizoLightboxReady) return;
    node.dataset.fizoLightboxReady = 'true';
    node.querySelector('[data-fizo-lightbox-close]')?.addEventListener('click', () => node.close());
    node.querySelector('[data-fizo-lightbox-prev]')?.addEventListener('click', () => renderPreview((node._index || 0) - 1));
    node.querySelector('[data-fizo-lightbox-next]')?.addEventListener('click', () => renderPreview((node._index || 0) + 1));
    node.addEventListener('click', (event) => { if (event.target === node) node.close(); });
    node.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') renderPreview((node._index || 0) - 1);
      if (event.key === 'ArrowRight') renderPreview((node._index || 0) + 1);
    });
    document.addEventListener('click', (event) => {
      if (window.FizoMediaViewer) return;
      const trigger = event.target.closest('[data-fizo-preview-item]');
      if (!trigger) return;
      event.preventDefault();
      openPreview(trigger);
    });
  };

  const initMaterials = () => {
    document.querySelectorAll('[data-fizo-materials]').forEach((root) => {
      if (root.dataset.fizoMaterialsReady) return;
      root.dataset.fizoMaterialsReady = 'true';
      const items = [...root.querySelectorAll('[data-fizo-material-item]')];
      const stageMedia = [...root.querySelectorAll('[data-fizo-material-stage]')];
      const desktopQuery = window.matchMedia('(min-width: 990px)');
      let activeStageIndex = Math.max(0, stageMedia.findIndex((media) => media.classList.contains('is-active')));
      let sectionVisible = true;
      const pauseStageVideo = (media) => {
        if (media instanceof HTMLVideoElement) media.pause();
      };
      const playStageVideo = (media) => {
        if (sectionVisible && media instanceof HTMLVideoElement) media.play().catch(() => {});
      };
      const setActiveItem = (item) => {
        items.forEach((node) => node.classList.toggle('is-active', node === item));
      };
      const activate = (item) => {
        const targetIndex = items.indexOf(item);
        if (!stageMedia.length || targetIndex < 0 || !stageMedia[targetIndex]) {
          setActiveItem(item);
          return;
        }
        const current = stageMedia[activeStageIndex];
        const next = stageMedia[targetIndex];
        if (current === next) {
          playStageVideo(next);
          setActiveItem(item);
          return;
        }
        next.classList.add('is-active');
        if (current) current.classList.remove('is-active');
        pauseStageVideo(current);
        playStageVideo(next);
        activeStageIndex = targetIndex;
        setActiveItem(item);
      };
      const observer = new IntersectionObserver((entries) => {
        if (desktopQuery.matches) return;
        const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) activate(visible.target);
      }, { rootMargin: '-32% 0px -42% 0px', threshold: [0.15, 0.5, 0.8] });
      let scrollFrame = null;
      const syncPinnedStep = () => {
        scrollFrame = null;
        if (!desktopQuery.matches || !items.length) return;
        const sectionTop = root.getBoundingClientRect().top + window.scrollY;
        const distance = Math.max(root.offsetHeight - window.innerHeight, 1);
        const progress = clamp((window.scrollY - sectionTop) / distance, 0, 0.9999);
        activate(items[Math.min(items.length - 1, Math.floor(progress * items.length))]);
      };
      const schedulePinnedStep = () => {
        if (scrollFrame !== null) return;
        scrollFrame = window.requestAnimationFrame(syncPinnedStep);
      };
      items.forEach((item) => {
        observer.observe(item);
        item.addEventListener('click', () => {
          activate(item);
        });
      });
      const sectionObserver = new IntersectionObserver((entries) => {
        sectionVisible = entries[0]?.isIntersecting || false;
        if (sectionVisible) {
          playStageVideo(stageMedia[activeStageIndex]);
        } else {
          stageMedia.forEach(pauseStageVideo);
        }
      }, { threshold: 0.1 });
      sectionObserver.observe(root);
      window.addEventListener('scroll', schedulePinnedStep, { passive: true });
      window.addEventListener('resize', schedulePinnedStep, { passive: true });
      if (items[0]) activate(items[0]);
      syncPinnedStep();
    });
  };

  const normalizeRecommendationProducts = (payload) => {
    if (Array.isArray(payload?.products)) return payload.products;
    if (Array.isArray(payload?.data?.products)) return payload.data.products;
    return [];
  };

  const readRecommendationPreview = (root) => {
    const preview = root.querySelector('[data-fizo-recommendations-preview]');
    if (!preview?.textContent) return [];
    try {
      return normalizeRecommendationProducts(JSON.parse(preview.textContent));
    } catch {
      return [];
    }
  };

  const initRecommendations = () => {
    document.querySelectorAll('[data-fizo-recommendations]').forEach((root) => {
      if (root.dataset.fizoRecommendationsReady) return;
      root.dataset.fizoRecommendationsReady = 'true';
      const endpoint = root.dataset.fizoRecommendationsEndpoint;
      const track = root.querySelector('[data-fizo-recommendations-track]');
      const sentinel = document.querySelector('[data-fizo-sticky-buy-stop]');
      if (!track || !root.dataset.productId) return;

      const previous = root.querySelector('[data-fizo-recommendations-prev]');
      const next = root.querySelector('[data-fizo-recommendations-next]');
      const syncControls = () => {
        const max = Math.max(track.scrollWidth - track.clientWidth, 0);
        previous?.toggleAttribute('disabled', track.scrollLeft <= 2);
        next?.toggleAttribute('disabled', track.scrollLeft >= max - 2);
      };
      let controlSyncTimer = null;
      const scheduleControlSync = () => {
        window.clearTimeout(controlSyncTimer);
        controlSyncTimer = window.setTimeout(syncControls, 100);
      };
      previous?.addEventListener('click', () => {
        track.scrollBy({ left: -track.clientWidth, behavior: reducedMotion ? 'auto' : 'smooth' });
      });
      next?.addEventListener('click', () => {
        track.scrollBy({ left: track.clientWidth, behavior: reducedMotion ? 'auto' : 'smooth' });
      });
      // Do not force layout work on every animation frame while the wide cards move.
      track.addEventListener('scroll', scheduleControlSync, { passive: true });
      track.addEventListener('scrollend', syncControls);
      new ResizeObserver(syncControls).observe(track);

      const usingShopifyRecommendations = !endpoint;
      const url = new URL(endpoint || '/recommendations/products.json', window.location.origin);
      url.searchParams.set('product_id', root.dataset.productId || '');
      url.searchParams.set('limit', root.dataset.limit || '4');
      if (usingShopifyRecommendations) {
        url.searchParams.set('intent', 'related');
      } else {
        url.searchParams.set('product_handle', root.dataset.productHandle || '');
        url.searchParams.set('variant_id', root.dataset.variantId || '');
      }
      const renderProducts = async (products) => {
        const urls = products.map((product) => product?.url
          || product?.productUrl
          || (product?.handle ? `/products/${product.handle}` : '')
          || (product?.productHandle ? `/products/${product.productHandle}` : '')
        ).filter(Boolean);
        const cards = await globalThis.FizoProductCardFragment?.fetchCards(urls) || [];
        if (!cards.length) return false;
        cards.forEach((card) => card.classList.add('fizo-pdp-recommendation-card'));
        track.replaceChildren(...cards);
        root.hidden = false;
        if (sentinel) sentinel.hidden = false;
        window.requestAnimationFrame(syncControls);
        return true;
      };
      fetch(url, { headers: { Accept: 'application/json' } })
        .then((response) => response.ok ? response.json() : Promise.reject(new Error(`Recommendation request failed: ${response.status}`)))
        .then(async (payload) => {
          const products = normalizeRecommendationProducts(payload);
          if (await renderProducts(products)) return;
          await renderProducts(readRecommendationPreview(root));
        })
        .catch(async () => {
          if (await renderProducts(readRecommendationPreview(root))) return;
          root.hidden = true;
          if (sentinel) sentinel.hidden = true;
        });
    });
  };

  const initStickyBuy = () => {
    const experimentActionSelector = '[data-fizo-experiment-group-a], [data-fizo-experiment-group-b]';
    const activeExperimentAction = (container) => (
      [...container.querySelectorAll(experimentActionSelector)].find((button) => !button.hidden)
    );

    document.querySelectorAll('[data-fizo-product-submit]').forEach((button) => {
      if (button.dataset.fizoProductSubmitReady) return;
      const submit = document.getElementById(button.dataset.fizoProductSubmit);
      button.addEventListener('click', () => submit?.closest('form')?.requestSubmit(submit));
      button.dataset.fizoProductSubmitReady = 'true';
    });

    document.querySelectorAll('[data-fizo-sticky-buy]').forEach((bar) => {
      if (bar.dataset.fizoStickyBuyReady) return;
      const submit = document.getElementById(bar.dataset.fizoSubmit);
      if (!submit) return;
      const hero = document.getElementById(bar.dataset.fizoStickyBuy);
      if (!hero) return;
      const setStickyVisibility = (visible) => {
        bar.classList.toggle('is-visible', visible);
        bar.setAttribute('aria-hidden', String(!visible));
      };
      let frame = null;
      const syncInlineAction = () => {
        frame = null;
        const inlineAction = activeExperimentAction(hero);
        const stickyAction = activeExperimentAction(bar);
        if (!inlineAction) {
          setStickyVisibility(false);
          return;
        }
        const inlineRect = inlineAction.getBoundingClientRect();
        const stickyActionTop = window.innerHeight - bar.offsetHeight + (stickyAction?.offsetTop || 0);
        setStickyVisibility(inlineRect.top > stickyActionTop + 4 || inlineRect.bottom <= 0);
      };
      const scheduleInlineAction = () => {
        if (frame !== null) return;
        frame = window.requestAnimationFrame(syncInlineAction);
      };
      window.addEventListener('scroll', scheduleInlineAction, { passive: true });
      window.addEventListener('resize', scheduleInlineAction, { passive: true });
      new MutationObserver(scheduleInlineAction).observe(hero, {
        attributes: true,
        subtree: true,
        attributeFilter: ['hidden'],
      });
      syncInlineAction();
      // Group A is delegated to the Fit Profile drawer. Group B receives its
      // add-to-bag handler after the experiment assignment has resolved.
      bar.dataset.fizoStickyBuyReady = 'true';
    });

    document.querySelectorAll('[data-fizo-desktop-sticky-buy]').forEach((bar) => {
      if (bar.dataset.fizoDesktopStickyBuyReady) return;
      const hero = document.getElementById(bar.dataset.fizoStickyHero);
      const stop = document.querySelector('[data-fizo-sticky-buy-stop]');
      if (!hero || !stop) return;

      let frame = null;
      const syncVisibility = () => {
        frame = null;
        const heroHasLeft = hero.getBoundingClientRect().bottom <= 0;
        const enteringRecommendations = !stop.hidden && stop.getBoundingClientRect().top <= window.innerHeight;
        const visible = heroHasLeft && !enteringRecommendations;
        bar.classList.toggle('is-visible', visible);
        bar.setAttribute('aria-hidden', String(!visible));
      };
      const scheduleVisibility = () => {
        if (frame !== null) return;
        frame = window.requestAnimationFrame(syncVisibility);
      };

      const prices = bar.querySelectorAll('[data-fizo-desktop-sticky-price]');
      let priceEntries = [];
      try {
        priceEntries = JSON.parse(bar.querySelector('[data-fizo-desktop-sticky-price-map]')?.textContent || '[]');
      } catch (error) {
        console.warn('FIZO sticky price data could not be parsed.', error);
      }
      const priceMap = new Map(priceEntries.map(({ id, price: value }) => [String(id), value]));
      const syncSelection = () => {
        const selected = hero.querySelector('[data-option-name="Color"]:checked, [data-option-name="Colour"]:checked, [data-option-name="颜色"]:checked');
        bar.querySelectorAll('[data-fizo-colour-mirror]').forEach((button) => {
          const active = button.dataset.fizoColourMirror === selected?.id;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
        const variant = JSON.parse(hero.querySelector('variant-selects [data-selected-variant]')?.textContent || 'null');
        if (variant?.id && priceMap.has(String(variant.id))) {
          prices.forEach((price) => { price.textContent = priceMap.get(String(variant.id)); });
        }
      };

      bar.querySelectorAll('[data-fizo-colour-mirror]').forEach((button) => {
        button.addEventListener('click', () => document.getElementById(button.dataset.fizoColourMirror)?.click());
      });
      hero.addEventListener('change', syncSelection);
      new MutationObserver(syncSelection).observe(hero, { childList: true, subtree: true, characterData: true });
      window.addEventListener('scroll', scheduleVisibility, { passive: true });
      window.addEventListener('resize', scheduleVisibility, { passive: true });
      syncSelection();
      syncVisibility();
      bar.dataset.fizoDesktopStickyBuyReady = 'true';
    });
  };

  const initPdpFitExperiment = () => {
    document.querySelectorAll('product-info.fizo-pdp').forEach((productInfo) => {
      if (productInfo.dataset.fizoPdpExperimentReady) return;
      productInfo.dataset.fizoPdpExperimentReady = 'true';

      let variants = [];
      try {
        variants = JSON.parse(productInfo.querySelector('[data-fizo-pdp-variants]')?.textContent || '[]');
      } catch (error) {
        console.warn('FIZO PDP variant data could not be parsed.', error);
      }
      if (!variants.length) return;

      const groupAActions = [
        ...productInfo.querySelectorAll('[data-fizo-experiment-group-a]'),
        ...document.querySelectorAll(`[data-fizo-sticky-buy="ProductInfo-${productInfo.dataset.section}"] [data-fizo-experiment-group-a]`),
        ...document.querySelectorAll(`[data-fizo-desktop-sticky-buy][data-fizo-sticky-hero="ProductInfo-${productInfo.dataset.section}"] [data-fizo-experiment-group-a]`),
      ];
      const groupBActions = [
        ...productInfo.querySelectorAll('[data-fizo-experiment-group-b]'),
        ...document.querySelectorAll(`[data-fizo-sticky-buy="ProductInfo-${productInfo.dataset.section}"] [data-fizo-experiment-group-b]`),
        ...document.querySelectorAll(`[data-fizo-desktop-sticky-buy][data-fizo-sticky-hero="ProductInfo-${productInfo.dataset.section}"] [data-fizo-experiment-group-b]`),
      ];
      const colourOptionIndex = Number(productInfo.dataset.fizoColourOptionPosition || 0) - 1;
      const fitBandOptionIndex = Number(productInfo.dataset.fizoFitBandOptionPosition || 0) - 1;
      const fitBandEnabled = productInfo.dataset.fizoFitBandEnabled === 'true';
      const getSelectedColour = () => productInfo.querySelector(
        '[data-option-name="Color"]:checked, [data-option-name="Colour"]:checked, [data-option-name="颜色"]:checked'
      )?.value;
      const getFirstAvailableVariant = () => {
        const selectedColour = getSelectedColour();
        return variants.find((variant) => {
          if (!variant.available) return false;
          if (colourOptionIndex >= 0 && selectedColour && variant.options?.[colourOptionIndex] !== selectedColour) return false;
          if (fitBandEnabled && fitBandOptionIndex >= 0) {
            const band = String(variant.options?.[fitBandOptionIndex] || '').toUpperCase().replace(/[\s/]+/g, '-');
            if (band !== 'D-M') return false;
          }
          return true;
        }) || (fitBandEnabled ? null : variants.find((variant) => variant.available));
      };
      const setLineProperty = (form, name, value) => {
        let input = form.querySelector(`input[name="properties[${name}]"]`);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${name}]`;
          form.appendChild(input);
        }
        input.value = value || '';
      };
      let currentExperimentAssignment = null;
      const submitFirstSize = (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();

        const variant = getFirstAvailableVariant();
        const form = productInfo.querySelector('product-form form');
        const submit = form?.querySelector('[type="submit"]');
        const variantInput = form?.querySelector('input[name="id"]');
        if (!variant || !form || !submit || !variantInput) return;

        variantInput.disabled = false;
        variantInput.value = String(variant.id);
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        setLineProperty(form, '_fizo_experiment', currentExperimentAssignment?.experimentKey || 'PDP_FIT_PROFILE_V1');
        setLineProperty(form, '_fizo_experiment_group', 'B');
        setLineProperty(form, '_fizo_experiment_assignment_id', currentExperimentAssignment?.assignmentId || '');
        form.requestSubmit(submit);
      };

      let groupBHandlerBound = false;
      const applyAssignment = (assignment) => {
        const showGroupB = window.FizoPdpExperiment?.isGroupBForProduct?.(
          assignment,
          productInfo.dataset.productHandle || ''
        ) === true;
        currentExperimentAssignment = assignment || null;
        productInfo.dataset.fizoExperimentKey = assignment?.experimentKey || 'PDP_FIT_PROFILE_V1';
        productInfo.dataset.fizoExperimentAssignmentId = assignment?.assignmentId || '';
        groupAActions.forEach((button) => { button.hidden = showGroupB; });
        groupBActions.forEach((button) => {
          button.hidden = !showGroupB;
          button.classList.toggle('is-pdp-experiment-add-to-bag', showGroupB);
          if (showGroupB && !groupBHandlerBound) {
            button.addEventListener('click', submitFirstSize, { capture: true });
          }
        });
        if (showGroupB) groupBHandlerBound = true;
      };

      applyAssignment(window.FizoPdpExperiment?.cached);
      window.FizoPdpExperiment?.ready?.then(applyAssignment);
    });
  };

  const initPdpFulfillment = () => {
    document.querySelectorAll('product-info.fizo-pdp[data-fizo-lead-time-endpoint]').forEach((productInfo) => {
      if (productInfo.dataset.fizoPdpFulfillmentReady) return;
      const fulfillment = productInfo.querySelector('[data-fizo-pdp-fulfillment]');
      const leadTimeValue = fulfillment?.querySelector('[data-fizo-lead-time-value]');
      const madeToOrder = fulfillment?.querySelector('[data-fizo-made-to-order]');
      const madeToOrderTrigger = fulfillment?.querySelector('[data-fizo-made-to-order-trigger]');
      const madeToOrderNote = fulfillment?.querySelector('[data-fizo-made-to-order-note]');
      const endpoint = productInfo.dataset.fizoLeadTimeEndpoint;
      if (!fulfillment || !leadTimeValue || !madeToOrder || !madeToOrderTrigger || !madeToOrderNote || !endpoint) return;

      productInfo.dataset.fizoPdpFulfillmentReady = 'true';
      const leadTimeFallback = leadTimeValue.textContent;
      let leadTimeController;
      const requestLeadTime = async (sku) => {
        leadTimeController?.abort();
        leadTimeValue.textContent = leadTimeFallback;
        if (!sku) return;

        leadTimeController = new AbortController();
        try {
          const response = await fetch(`${endpoint}?sku=${encodeURIComponent(sku)}&country_code=us`, {
            headers: { accept: 'application/json' },
            signal: leadTimeController.signal,
          });
          if (!response.ok) throw new Error(`Lead time request failed: ${response.status}`);
          const { data } = await response.json();
          const leadTime = String(data?.lead_time || '').trim();
          if (leadTime) leadTimeValue.textContent = ` ${leadTime}`;
        } catch (error) {
          if (error.name !== 'AbortError') console.warn('FIZO lead time could not be loaded.', error);
        }
      };

      const fineHover = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      const setNoteOpen = (open) => {
        madeToOrderNote.hidden = !open;
        madeToOrderTrigger.setAttribute('aria-expanded', String(open));
      };
      madeToOrder.addEventListener('pointerenter', () => {
        if (fineHover()) setNoteOpen(true);
      });
      madeToOrder.addEventListener('pointerleave', () => {
        if (fineHover()) setNoteOpen(false);
      });
      madeToOrderTrigger.addEventListener('focus', () => {
        if (fineHover()) setNoteOpen(true);
      });
      madeToOrderTrigger.addEventListener('blur', () => {
        if (fineHover()) setNoteOpen(false);
      });
      madeToOrderTrigger.addEventListener('click', () => {
        if (!fineHover()) setNoteOpen(madeToOrderNote.hidden);
      });

      let unsubscribeVariantChange;
      unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (!productInfo.isConnected) {
          unsubscribeVariantChange();
          return;
        }
        if (String(data?.sectionId) !== String(productInfo.dataset.section)) return;
        void requestLeadTime(data?.variant?.sku || '');
      });
      void requestLeadTime(productInfo.dataset.fizoInitialSku || '');
    });
  };

  const initProductMedia = () => {
    document.querySelectorAll('[data-fizo-media-carousel]').forEach((carousel) => {
      if (carousel.dataset.fizoMediaReady) return;
      const slides = [...carousel.querySelectorAll('[data-fizo-media-slide]')];
      if (!slides.length) return;
      const productInfo = carousel.closest('product-info');
      const stage = carousel.querySelector('[data-fizo-media-stage]');
      const progress = carousel.querySelector('[data-fizo-media-progress]');
      const normaliseColour = (value) => String(value || '').trim().toLowerCase();
      let activeColour = normaliseColour(
        productInfo?.querySelector('[data-option-name="Color"]:checked, [data-option-name="Colour"]:checked, [data-option-name="颜色"]:checked')?.value
      );
      const activeSlides = () => {
        const colourSlides = slides.filter((slide) => normaliseColour(slide.dataset.fizoMediaColour) === activeColour);
        return colourSlides.length ? colourSlides : slides;
      };
      let index = Math.max(0, activeSlides().findIndex((slide) => slide.classList.contains('is-active')));
      const counter = carousel.querySelector('[data-fizo-media-count]');
      const syncColourSlides = () => {
        const visibleSlides = activeSlides();
        slides.forEach((slide) => slide.classList.toggle('is-colour-active', visibleSlides.includes(slide)));
        if (progress) {
          progress.replaceChildren(...visibleSlides.map((slide, slideIndex) => {
            const indicator = document.createElement('span');
            indicator.classList.toggle('is-active', slideIndex === index);
            return indicator;
          }));
        }
      };
      const syncMobileProgress = () => {
        if (!stage || !window.matchMedia('(max-width: 989px)').matches) return;
        const visibleSlides = activeSlides();
        const nextIndex = Math.min(visibleSlides.length - 1, Math.max(0, Math.round(stage.scrollLeft / stage.clientWidth)));
        index = nextIndex;
        progress?.querySelectorAll('span').forEach((indicator, indicatorIndex) => {
          indicator.classList.toggle('is-active', indicatorIndex === nextIndex);
        });
      };
      const show = (next) => {
        const visibleSlides = activeSlides();
        if (!visibleSlides.length) return;
        // Wrap in both directions so the lower-left controls behave as a loop.
        index = ((next % visibleSlides.length) + visibleSlides.length) % visibleSlides.length;
        const nextImage = visibleSlides[index].querySelector('img');
        if (nextImage) {
          nextImage.loading = 'eager';
          nextImage.fetchPriority = 'high';
        }
        slides.forEach((slide) => slide.classList.toggle('is-active', slide === visibleSlides[index]));
        if (counter) counter.textContent = `${index + 1} / ${visibleSlides.length}`;
        syncColourSlides();
      };
      const syncColourMedia = () => {
        const selected = productInfo?.querySelector('[data-option-name="Color"]:checked, [data-option-name="Colour"]:checked, [data-option-name="颜色"]:checked');
        const nextColour = normaliseColour(selected?.value);
        if (!nextColour || nextColour === activeColour) return;
        const nextSlides = slides.filter((slide) => normaliseColour(slide.dataset.fizoMediaColour) === nextColour);
        if (!nextSlides.length) return;
        activeColour = nextColour;
        index = 0;
        show(index);
        if (stage && window.matchMedia('(max-width: 989px)').matches) stage.scrollTo({ left: 0, behavior: 'auto' });
      };
      show(index);
      carousel.querySelector('[data-fizo-media-prev]')?.addEventListener('click', () => show(index - 1));
      carousel.querySelector('[data-fizo-media-next]')?.addEventListener('click', () => show(index + 1));
      stage?.addEventListener('scroll', syncMobileProgress, { passive: true });
      productInfo?.addEventListener('change', syncColourMedia);
      carousel.dataset.fizoMediaReady = 'true';
    });
  };

  const ensureJunip = (shop) => {
    if (window.junip) return Promise.resolve();
    const existing = document.getElementById('fizo-junip-widget-script');
    if (existing) {
      return new Promise((resolve) => existing.addEventListener('load', resolve, { once: true }));
    }
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.id = 'fizo-junip-widget-script';
      script.src = `https://widgets.juniphq.com/v1/junip_shopify.js?shop=${encodeURIComponent(shop)}`;
      script.async = true;
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', resolve, { once: true });
      document.head.append(script);
    });
  };

  const initProductDetailPanels = () => {
    document.querySelectorAll('[data-fizo-detail-panel]').forEach((panel) => {
      if (panel.dataset.fizoDetailPanelReady) return;
      panel.dataset.fizoDetailPanelReady = 'true';
      const junipRoot = panel.querySelector('[data-fizo-junip-root]');
      const contentViews = [...panel.querySelectorAll('[data-fizo-detail-panel-content]')];
      const renderReviewScale = () => {
        const average = panel.querySelector('.junip-product-review-average');
        if (!average || average.dataset.fizoReviewScaleReady) return;
        average.dataset.fizoReviewScaleReady = 'true';
        const scale = document.createElement('span');
        scale.className = 'fizo-review-rating-scale';
        scale.innerHTML = '<span class="fizo-review-rating-scale__slash" aria-hidden="true">/</span><span>5.0</span>';
        average.append(scale);
      };
      renderReviewScale();
      const triggers = [...document.querySelectorAll('[data-fizo-detail-panel-trigger]')]
        .filter((trigger) => trigger.getAttribute('aria-controls') === panel.id);
      const syncReviewCount = () => {
        const count = readJunipProductReviewCount(junipRoot);
        triggers.forEach((trigger) => {
          trigger.querySelectorAll('[data-fizo-reviews-count]').forEach((target) => {
            target.textContent = count === null ? '' : String(count);
          });
        });
      };
      const refreshJunipReviewUi = () => {
        renderReviewScale();
        syncReviewCount();
      };
      refreshJunipReviewUi();
      if (junipRoot) new MutationObserver(refreshJunipReviewUi).observe(junipRoot, { childList: true, subtree: true });
      const backdrop = document.querySelector(`[data-fizo-detail-panel-backdrop="${panel.id}"]`);
      // The PDP content wrapper creates a lower stacking context than the global header.
      // Portal the drawer pair to body so the overlay can sit above every page component.
      if (backdrop && panel.parentElement !== document.body) {
        document.body.append(backdrop, panel);
      }
      const setView = (view) => {
        const nextView = contentViews.some((content) => content.dataset.fizoDetailPanelContent === view) ? view : 'shipping';
        contentViews.forEach((content) => content.classList.toggle('is-active', content.dataset.fizoDetailPanelContent === nextView));
        triggers.forEach((trigger) => {
          const isActive = trigger.dataset.fizoDetailPanelView === nextView;
          trigger.setAttribute('aria-selected', String(isActive));
          trigger.setAttribute('aria-current', isActive ? 'page' : 'false');
        });
        if (nextView === 'reviews') ensureJunip(panel.dataset.fizoJunipShop || 'fizorun.com');
      };
      const close = () => {
        panel.classList.remove('is-open');
        panel.setAttribute('aria-hidden', 'true');
        backdrop?.classList.remove('is-open');
        triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
        document.documentElement.classList.remove('fizo-product-detail-panel-open');
        document.body.classList.remove('fizo-product-detail-panel-open');
      };
      const open = (view) => {
        setView(view);
        panel.classList.add('is-open');
        panel.setAttribute('aria-hidden', 'false');
        backdrop?.classList.add('is-open');
        triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
        document.documentElement.classList.add('fizo-product-detail-panel-open');
        document.body.classList.add('fizo-product-detail-panel-open');
      };
      triggers.forEach((trigger) => trigger.addEventListener('click', () => open(trigger.dataset.fizoDetailPanelView)));
      panel.querySelector('[data-fizo-detail-panel-close]')?.addEventListener('click', close);
      backdrop?.addEventListener('click', close);
      let touchStartX = 0;
      let touchStartY = 0;
      panel.addEventListener('touchstart', (event) => {
        const touch = event.changedTouches[0];
        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
      }, { passive: true });
      panel.addEventListener('touchend', (event) => {
        if (!window.matchMedia('(max-width: 989px)').matches || !panel.classList.contains('is-open')) return;
        const touch = event.changedTouches[0];
        const distanceX = touch.clientX - touchStartX;
        const distanceY = touch.clientY - touchStartY;
        // A deliberate right swipe closes the current panel without hijacking vertical scrolling.
        if (distanceX > 72 && Math.abs(distanceX) > Math.abs(distanceY)) close();
      }, { passive: true });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && panel.classList.contains('is-open')) close();
      });
      ensureJunip(panel.dataset.fizoJunipShop || 'fizorun.com');
    });
  };

  const init = () => {
    initCarousels();
    initVideos();
    initLightbox();
    initMaterials();
    initRecommendations();
    initPdpFulfillment();
    initPdpFitExperiment();
    initStickyBuy();
    initProductMedia();
    initProductDetailPanels();
  };
  document.addEventListener('shopify:section:load', init);
  new MutationObserver(init).observe(document.documentElement, { childList: true, subtree: true });
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init, { once: true }) : init();
})();
